const FEATURE_TYPES = {
    0: 'Conversion',
    1: 'Text',
    2: 'Choice',
    3: 'Multichoice',
    4: 'Translation',
    5: 'Boolean',
    6: 'BigText',
    7: 'Rating',
    8: 'Numeric',
};

const normalizeFeatureType = (type) => {
    if (typeof type === 'number') {
        return FEATURE_TYPES[type] || String(type);
    }
    return type;
};

const getPlainDisplayValue = (displayValue, separator = ' - ') => {
    if (displayValue === null || displayValue === undefined) {
        return '';
    }
    if (Array.isArray(displayValue)) {
        return displayValue.join(separator);
    }
    if (typeof displayValue === 'object') {
        return Object.values(displayValue).join(separator);
    }
    return String(displayValue);
};

const isConversionFeature = (ftItem) => {
    return Boolean(ftItem.C || ftItem.CN);
};

const ftItemToDisplayValues = (ftItem) => {
    const { C, CN, DV } = ftItem;
    if (isConversionFeature({ C, CN }) && DV && typeof DV === 'object' && !Array.isArray(DV)) {
        return Object.entries(DV).map(([metric, value]) => ({ metric, value: String(value) }));
    }
    return [{ metric: '', value: getPlainDisplayValue(DV) }];
};

const getMetricText = (value, metricItem) => {
    let suffix = '';
    if (metricItem) {
        if (metricItem.indexOf(':') > 0) {
            suffix = ' ' + metricItem.split(':')[1];
        }
    } else if (value.metric) {
        suffix = ' ' + value.metric;
    }
    return value.value + suffix;
};

const findDisplayValue = (displayValues, metricItem) => {
    return displayValues.find((displayValue) => {
        if (metricItem.indexOf(':') >= 0) {
            return displayValue.metric === metricItem.split(':')[0];
        }
        return displayValue.metric === metricItem;
    });
};

const matchesConversionName = (configConversionName, featureConversionName) => {
    if (!configConversionName || !featureConversionName) {
        return false;
    }
    if (configConversionName.indexOf('*') >= 0) {
        return featureConversionName.startsWith(configConversionName.split('*')[0]);
    }
    return configConversionName === featureConversionName;
};

const findMetricConfigByConversionName = (conversionName, modelGender, featuresConfiguration) => {
    const entries = (featuresConfiguration && featuresConfiguration.metrics) || [];
    return entries.find((entry) => {
        if (modelGender && entry.gender) {
            if (Number(entry.gender) !== Number(modelGender)) {
                return false;
            }
        }
        return matchesConversionName(entry.conversionName, conversionName);
    }) || null;
};

const isHiddenMetric = (metricName, modelGender) => {
    if (modelGender === 1 && metricName === 'USM') {
        return true;
    }
    if (modelGender === 2 && metricName === 'USW') {
        return true;
    }
    return false;
};

const getMetricName = (metric, conversionName, featuresConfiguration, modelGender) => {
    let metricname = metric;
    if (conversionName) {
        const conversionConfiguration = findMetricConfigByConversionName(conversionName, modelGender, featuresConfiguration);
        if (conversionConfiguration) {
            for (const metricItem of conversionConfiguration.metrics) {
                if (metricItem.indexOf(':') >= 0) {
                    if (metric === metricItem.split(':')[0]) {
                        metricname = metricItem.split(':')[1];
                    }
                }
            }
        }
    }
    if (metric === 'USW' || metric === 'USM') {
        metricname = 'US';
    }
    return metricname;
};

const formatFormattedValues = (displayValues, conversionConfig, modelGender) => {
    // il filtro di genere sta qui e non nei soli componenti: riepilogo e drawer
    // devono scegliere la stessa metrica anche quando nessuna config corrisponde
    const visibleValues = displayValues.filter((displayValue) => !isHiddenMetric(displayValue.metric, modelGender));
    if (conversionConfig) {
        const formatted = [];
        for (const metricItem of conversionConfig.metrics) {
            const displayValue = findDisplayValue(visibleValues, metricItem);
            if (displayValue) {
                formatted.push(getMetricText(displayValue, metricItem));
            }
        }
        return formatted;
    }
    return visibleValues.map((displayValue) => getMetricText(displayValue));
};

const formatFeatureForDisplay = (ftItem, featuresConfiguration, modelGender) => {
    const { ID, N, V, CN, DV } = ftItem;
    const isConversion = isConversionFeature(ftItem);
    const displayValues = ftItemToDisplayValues(ftItem);
    const conversionConfig = isConversion && CN
        ? findMetricConfigByConversionName(CN, modelGender, featuresConfiguration)
        : null;
    const formattedValues = isConversion
        ? formatFormattedValues(displayValues, conversionConfig, modelGender)
        : [getPlainDisplayValue(DV)];

    const hasConversions = Boolean(isConversion && displayValues.some((val) => val.metric));

    return {
        id: ID,
        displayName: N,
        // valore grezzo: i tipi Boolean e Rating si rendono da questo, non dal display value
        value: V,
        conversionName: CN || null,
        formattedValues,
        displayValues,
        hasConversions,
        type: normalizeFeatureType(ftItem.T),
    };
};

const getModelFeaturesFromFt = (ftArray, featuresConfiguration, modelGender) => {
    if (!Array.isArray(ftArray) || ftArray.length === 0) {
        return [];
    }

    const writtenFeatures = [];
    const allFeatures = !featuresConfiguration.selectedFeatures || featuresConfiguration.selectedFeatures.length === 0;
    const filteredFeatures = [];

    for (const ftItem of ftArray) {
        if (!ftItem || !ftItem.N) {
            continue;
        }
        if (writtenFeatures.includes(ftItem.N)) {
            continue;
        }
        if (!allFeatures && !featuresConfiguration.selectedFeatures.includes(ftItem.N)) {
            continue;
        }
        writtenFeatures.push(ftItem.N);
        filteredFeatures.push(formatFeatureForDisplay(ftItem, featuresConfiguration, modelGender));
    }

    return filteredFeatures;
};

const hasConversionFeatures = (modelFeatures) => {
    return modelFeatures.some((feature) => feature.hasConversions);
};

export {
    FEATURE_TYPES,
    normalizeFeatureType,
    getPlainDisplayValue,
    isConversionFeature,
    ftItemToDisplayValues,
    matchesConversionName,
    findMetricConfigByConversionName,
    formatFeatureForDisplay,
    getModelFeaturesFromFt,
    getMetricText,
    getMetricName,
    isHiddenMetric,
    hasConversionFeatures,
};
